import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Play } from 'lucide-react';
import { useState } from 'react';
import { Badge, Button, Card, Empty, ErrorNote, Field, Loading, Select } from '@/components/ui';
import { api, apiStream } from '@/lib/api';
import { fmtMs, fmtUsd } from '@/lib/format';
import { canEdit, rk, useMe, useModels } from '@/lib/hooks';
import { getRegion, streamEndpoint } from '@/lib/regions';
import type { AgentDetail, TestCase, TestResult } from '@/lib/types';

const TONE = { same: 'ok', changed: 'warn', error: 'bad' } as const;
const MAX_MODELS = 3;

type Cell = { status: 'same' | 'changed' | 'error'; durationMs: number | null; costUsd: number | null; traceId: string | null; error?: string | null };

/**
 * Control board: run this agent's test cases on several models and compare,
 * case by case, with the approved baseline — plus cost and speed. Nothing is
 * published; switching the agent's model is a normal draft → publish.
 */
export const AgentCompare = ({ detail }: { detail: AgentDetail }) => {
  const me = useMe();
  const models = useModels();
  const agentId = detail.agent.id;
  const cases = useQuery({ queryKey: rk('test-cases', agentId), queryFn: () => api<{ cases: TestCase[] }>(`/v1/agents/${agentId}/test-cases`) });
  const live = detail.versions.find((v) => v.id === detail.agent.publishedVersionId) ?? detail.versions[0];
  const [versionId, setVersionId] = useState(live?.id ?? '');
  const currentModel = (detail.versions.find((v) => v.id === versionId)?.config as { model?: { name?: string } } | undefined)?.model?.name;
  const [picked, setPicked] = useState<string[]>(currentModel ? [currentModel] : []);
  const [cells, setCells] = useState<Record<string, Record<string, Cell>>>({});
  const [running, setRunning] = useState<string | null>(null);
  const url = streamEndpoint(getRegion(), '/v1/test-runs/stream');
  const isFlow = detail.agent.kind === 'flow';

  const run = useMutation({
    mutationFn: async () => {
      setCells({});
      for (const model of picked) {
        setRunning(model);
        let runId: string | null = null;
        let failure: string | null = null;
        await apiStream(url!, { agentId, versionId, model }, (e) => {
          if (e.type === 'start') runId = e.runId;
          else if (e.type === 'error') failure = e.message;
        });
        if (failure) throw new Error(`${model}: ${failure}`);
        if (!runId) continue;
        const res = await api<{ results: TestResult[] }>(`/v1/test-runs/${runId}`);
        setCells((prev) => ({
          ...prev,
          [model]: Object.fromEntries(res.results.map((r) => [r.caseId, { status: r.status, durationMs: r.durationMs, costUsd: r.costUsd, traceId: r.traceId, error: r.error }])),
        }));
      }
      setRunning(null);
    },
    onError: () => setRunning(null),
  });

  if (cases.isLoading || models.isLoading) return <Loading />;
  const list = (cases.data?.cases ?? []).slice(0, 25);
  const enabled = (models.data?.models ?? []).filter((m) => m.enabled);
  if (isFlow) return <Empty title="Compare models on the agents in this flow">A flow runs several agents, each with its own model.</Empty>;
  if (!list.length)
    return (
      <Empty title="No test cases yet">
        Save requests as test cases (or approve reviewed content — approvals are saved automatically), then compare models on them here.
      </Empty>
    );

  const toggle = (name: string) => setPicked((p) => (p.includes(name) ? p.filter((x) => x !== name) : p.length >= MAX_MODELS ? p : [...p, name]));
  const totals = (model: string) => {
    const c = Object.values(cells[model] ?? {});
    return {
      same: c.filter((x) => x.status === 'same').length,
      changed: c.filter((x) => x.status === 'changed').length,
      error: c.filter((x) => x.status === 'error').length,
      cost: c.reduce((a, x) => a + (x.costUsd ?? 0), 0),
      avgMs: c.length ? c.reduce((a, x) => a + (x.durationMs ?? 0), 0) / c.length : null,
    };
  };

  return (
    <div className="flex flex-col gap-5">
      <Card title="Compare models">
        <div className="flex flex-col gap-3 p-4">
          <Field label="Prompt from version">
            <Select value={versionId} onChange={(e) => setVersionId(e.target.value)}>
              {detail.versions.map((v) => (
                <option key={v.id} value={v.id}>
                  v{v.version} {v.status === 'draft' ? '(draft)' : v.id === detail.agent.publishedVersionId ? '(live)' : ''}
                </option>
              ))}
            </Select>
          </Field>
          <div>
            <div className="mb-1 text-xs font-medium text-ink-2">Models (up to {MAX_MODELS})</div>
            <div className="flex flex-wrap gap-2">
              {enabled.map((m) => (
                <label key={m.id} className="flex items-center gap-1.5 rounded border border-line px-2 py-1 text-[13px]">
                  <input type="checkbox" checked={picked.includes(m.name)} onChange={() => toggle(m.name)} disabled={!picked.includes(m.name) && picked.length >= MAX_MODELS} />
                  {m.label ?? m.name}
                  {m.name === currentModel && <Badge>current</Badge>}
                </label>
              ))}
            </div>
          </div>
          <Button variant="primary" className="self-start" disabled={!url || !canEdit(me.data) || picked.length === 0} loading={run.isPending} onClick={() => run.mutate()}>
            <Play className="size-3.5" /> Run {list.length} case{list.length === 1 ? '' : 's'} on {picked.length} model{picked.length === 1 ? '' : 's'}
          </Button>
          {running && <p className="text-xs text-ink-3">Running on {running}…</p>}
          {run.error ? <ErrorNote error={run.error} /> : null}
          <p className="text-xs text-ink-3">
            Calls the real models with this version’s prompt; code tools are stubbed. “Same” matches the saved baseline (for reviewed content, the version a person approved). “Changed” isn’t necessarily worse — open it to read.
          </p>
        </div>
      </Card>

      {Object.keys(cells).length > 0 && (
        <Card title="Results">
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="text-left text-xs text-ink-3">
                <tr>
                  <th className="px-4 py-2 font-medium">Case</th>
                  {picked.map((m) => (
                    <th key={m} className="px-3 py-2 font-medium">
                      {m}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {list.map((c) => (
                  <tr key={c.id} className="border-t border-line-2">
                    <td className="max-w-[320px] truncate px-4 py-2">{c.name}</td>
                    {picked.map((m) => {
                      const cell = cells[m]?.[c.id];
                      return (
                        <td key={m} className="px-3 py-2">
                          {cell ? (
                            <span className="flex items-center gap-2" title={cell.error ?? undefined}>
                              <Badge tone={TONE[cell.status]}>{cell.status}</Badge>
                              <span className="text-xs text-ink-3">
                                {fmtMs(cell.durationMs)} · {fmtUsd(cell.costUsd)}
                              </span>
                              {cell.traceId && (
                                <Link to="/requests/$traceId" params={{ traceId: cell.traceId }} className="text-xs hover:underline">
                                  open
                                </Link>
                              )}
                            </span>
                          ) : (
                            <span className="text-xs text-ink-3">—</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                <tr className="border-t border-line bg-line-2/40 font-medium">
                  <td className="px-4 py-2">Total</td>
                  {picked.map((m) => {
                    const t = totals(m);
                    return (
                      <td key={m} className="px-3 py-2 text-xs">
                        {t.same} same · {t.changed} changed · {t.error} failed
                        <br />
                        {fmtUsd(t.cost)} · avg {fmtMs(t.avgMs)}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};
